import {
  hashEmail,
  MarieSchemaAnalytics,
  Environment
} from '@atlassiansox/bxpkit-core';
import React, { useEffect, useState } from 'react';
import { isProductIncluded, useUserInfo } from '../../../../hook';
import {
  ContainerLayoutType,
  MigrationDataType,
  ProductKeys,
  WacSignupLayoutType
} from '../../../../model';
import { getMigrationData, SignupLayoutAnalytics } from '../../../../service';
import {
  LayoutDefault,
  LayoutDevops,
  LayoutFirstImpressions,
  LayoutStandalone
} from './container';
import {
  useAppDispatch,
  useAppSelector,
  updateDeferredSiteNameStatus
} from '../../../../redux';
import { ProductKeysType } from '@atlassiansox/bxpkit-core';

export function SignUpLayoutContainer(props: WacSignupLayoutType) {
  const {
    layout,
    labels,
    analyticsTrack,
    productKeys,
    templateId,
    isMigrationSignup,
    queryParameters,
    productOverride,
    editionOverride,
    bundleOverride,
    locale,
    performanceStartTime,
    siteRenamePageUrl,
    firstImpressionsImageUrls
  } = props;

  const [combinedProps, setCombinedProps] = useState<ContainerLayoutType>();
  const [migrationData, setMigrationData] = useState<MigrationDataType>();
  const [formExistingSiteShown, setFormExistingSiteShown] = useState(true);

  const dispatch = useAppDispatch();

  const { enableDeferredSiteName } = useAppSelector(
    ({ featureFlags }) => featureFlags
  );

  // marie schema state values
  const [eventContainer, setEventContainer] = useState('wac-signup-form');
  const [suiteName, setSuiteName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const setEmailWithHash = async (email: string) => {
    const hash = await hashEmail(email);
    setUserEmail(hash);
  };

  let userInfo = useUserInfo();

  const combineProps = () => {
    setCombinedProps(
      Object.assign({}, props, {
        locale,
        userInfo,
        productKeys,
        migrationData,
        isMigrationSignup,
        formExistingSiteShown,
        setFormExistingSiteShown,
        siteRenamePageUrl: siteRenamePageUrl || '',
        firstImpressionsImageUrls: firstImpressionsImageUrls || {},
        templateId,
        edition: editionOverride
      })
    );
  };

  // migrationData update (if applicable)
  useEffect(() => {
    if (migrationData) {
      userInfo = Object.assign(userInfo, {
        extendedTrialOverridableSites: migrationData?.migrationSites
      });
      combineProps();
    }
  }, [migrationData]);

  // userInfo update
  useEffect(() => {
    combineProps();
    if (!userInfo) {
      return;
    }
    if (userInfo.email) {
      void setEmailWithHash(userInfo.email);
    }
    if (isMigrationSignup) {
      getMigrationData({
        queryParameters,
        labels,
        productKeys,
        userInfo,
        analyticsTrack
      })
        .then((data) => {
          if (data != null) {
            setMigrationData(data);
          }
        })
        .catch(() => setMigrationData({} as MigrationDataType));
    }

    // instantiate Analytics Singleton
    // tslint:disable-next-line: no-unused-expression
    new SignupLayoutAnalytics({
      analyticsTrack,
      performanceStartTime,
      isLoggedIn: userInfo.isLoggedIn
    });

    // Redirect if Beacon user is signed out
    const isBeacon = isProductIncluded(productKeys, ProductKeys.BEACON);
    const isBitbucket =
      productKeys.length === 1 &&
      isProductIncluded(productKeys, ProductKeys.BITBUCKET);

    if (
      !userInfo.isLoggedIn &&
      (isBeacon || isBitbucket || isMigrationSignup)
    ) {
      const redirectUrl = Environment.getIdSigninUrl();

      window.location.assign(redirectUrl);
    }
  }, [userInfo]);

  // productKeys update
  useEffect(() => {
    combineProps();
  }, [bundleOverride, productOverride]);

  let contextValues = {
    eventContainer,
    setEventContainer,
    suiteName,
    setSuiteName,
    userEmail,
    analyticsTrack,
    setUserEmail: setEmailWithHash,
    productKey: productKeys,
    productEdition: productKeys[0].edition,
    schemaVersion: 1,
    loginUserDetected: userInfo?.isLoggedIn ? 1 : 0,
    productsOffered: productKeys.map((prod: ProductKeysType) => prod.product),
    productPlatform: 'cloud'
  };

  // update contextValues when productKeys update
  useEffect(() => {
    contextValues = { ...contextValues, productKey: productKeys };
  }, [productKeys]);

  // update deferred site name status after productKeys set
  useEffect(() => {
    dispatch(updateDeferredSiteNameStatus());
  }, [enableDeferredSiteName]);
  useEffect(() => {
    const signupSource = new URLSearchParams(window.location.search).get(
      'signupSource'
    );
    if (signupSource === 'bbbundle') {
      analyticsTrack.segment('wac-signup-transition-from-bitbucket-bundles', {
        eventType: 'track',
        eventContainer: 'wac-sign-up-form',
        productKey: productKeys,
        productsOffered: contextValues.productsOffered,
        loginUserDetected: contextValues.loginUserDetected,
        userEmail: contextValues.userEmail,
        suiteName: contextValues.suiteName,
        productEdition: contextValues.productEdition,
        productPlatform: contextValues.productPlatform,
        schemaVersion: contextValues.schemaVersion
      });
    }
  }, []);

  return (
    <MarieSchemaAnalytics.Provider value={contextValues}>
      {(() => {
        if (!combinedProps) {
          return <></>;
        }

        switch (layout) {
          case 'standalone':
            return <LayoutStandalone {...combinedProps} />;
          case 'devops':
            return <LayoutDevops {...combinedProps} />;
          case 'first-impressions':
            return <LayoutFirstImpressions {...combinedProps} />;
          case 'default':
          default:
            return <LayoutDefault {...combinedProps} />;
        }
      })()}
    </MarieSchemaAnalytics.Provider>
  );
}
